// F4: Inference Hotspot in chat bubbles — per-message tint + tooltip.
// (The F2 What-If composer lives in annotated-composer.jsx; the
// pre-send popup sheet this file used to own was retired when D2
// became a hint-timing axis with no popup level.)

import { Icons, InferenceFreshness, RiskBadge } from './ui-atoms.jsx';
import { Confidence } from './extensions.jsx';

// F4 - Hotspot tooltip
export function HotspotTooltip({ msg, onClose, inferenceTriggerTime = null }) {
  if (!msg.inferences?.length) return null;
  return (
    <div
      style={{
        marginTop: 6,
        padding: 12,
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        boxShadow: '0 4px 12px oklch(25% 0.02 260 / 0.08)',
        animation: 'pop-in 180ms ease-out',
        maxWidth: 270,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Inferred from this message</span>
          {inferenceTriggerTime && (
            <>
              <span>·</span>
              <InferenceFreshness triggerTime={inferenceTriggerTime} style={{ fontSize: 9 }} />
            </>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            color: 'var(--ink-muted)',
          }}
        >
          {Icons.close(14, 'currentColor')}
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {msg.inferences.map((inf, i) => (
          <div key={i}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 3,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600 }}>{inf.trait}</span>
              <Confidence value={inf.confidence} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.4 }}>
              {inf.value}
            </div>
            <div
              className="mono"
              style={{
                fontSize: 10,
                color: 'var(--ink-muted)',
                marginTop: 4,
                fontStyle: 'italic',
                lineHeight: 1.4,
              }}
            >
              {inf.why}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Typing indicator — the "them" party is composing a reply. Mirrors a
// left-aligned "them" bubble (see MessageBubble: paper bg, tail-left radius,
// soft shadow) with three dots that pulse via the pm-typing-dot keyframes.
// Shown only during a reply's typing phase (driven by useInference.replyTyping).
export function TypingIndicator() {
  return (
    <div
      role="status"
      aria-label="Contact is typing"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '11px 14px',
          borderRadius: '16px 16px 16px 4px',
          background: 'var(--paper)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="pm-typing-dot"
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--ink-muted)',
              animationDelay: `${i * 160}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// F4 - Message bubble with hotspot highlighting
export function MessageBubble({
  msg,
  expanded,
  onToggle,
  hotspotsOn,
  inferenceTriggerTime = null,
}) {
  const isMe = msg.from === 'me';
  const risk = hotspotsOn ? msg.risk : 0;

  const tintBg =
    risk === 0
      ? null
      : risk === 1
        ? 'var(--risk-1-bg)'
        : risk === 2
          ? 'var(--risk-2-bg)'
          : 'var(--risk-3-bg)';
  const edgeColor =
    risk === 0
      ? null
      : risk === 1
        ? 'var(--risk-1-edge)'
        : risk === 2
          ? 'var(--risk-2-edge)'
          : 'var(--risk-3-edge)';

  // Base bubble colors (no hotspot tint)
  const baseBg = isMe ? 'var(--indigo)' : 'var(--paper)';
  const baseFg = isMe ? 'var(--paper)' : 'var(--ink)';

  // With hotspot, blend: outgoing keeps indigo text on tint bg.
  const bubbleBg = tintBg || baseBg;
  const bubbleFg = tintBg ? 'var(--ink)' : baseFg;

  // F4 gate: when hotspots are off (either the toggle or F4-disabled at
  // the app level), the bubble is non-interactive — no tap-to-reveal,
  // no inference tooltip. So /mirror and /risk stay clean of F4 UI.
  const clickable = hotspotsOn && !!msg.inferences?.length;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMe ? 'flex-end' : 'flex-start',
        gap: 2,
      }}
    >
      <div
        onClick={clickable ? onToggle : undefined}
        style={{
          maxWidth: '78%',
          padding: '9px 13px',
          borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          background: bubbleBg,
          color: bubbleFg,
          fontSize: 14,
          lineHeight: 1.4,
          cursor: clickable ? 'pointer' : 'default',
          position: 'relative',
          border: tintBg ? `1px solid ${edgeColor}` : '1px solid transparent',
          boxShadow: tintBg ? 'none' : isMe ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
          transition: 'background 200ms ease, border-color 200ms ease',
        }}
      >
        {msg.text}
        {clickable && hotspotsOn && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 6,
              paddingTop: 6,
              borderTop: `1px solid ${edgeColor}55`,
            }}
          >
            <RiskBadge level={risk} />
            <span className="mono" style={{ fontSize: 10, color: 'var(--ink-soft)' }}>
              {msg.inferences.length} inference{msg.inferences.length > 1 ? 's' : ''} · tap
            </span>
          </div>
        )}
      </div>
      <div
        className="mono"
        style={{
          fontSize: 9,
          color: 'var(--ink-muted)',
          padding: '0 6px',
        }}
      >
        {msg.time}
      </div>
      {expanded && hotspotsOn && (
        <HotspotTooltip msg={msg} onClose={onToggle} inferenceTriggerTime={inferenceTriggerTime} />
      )}
    </div>
  );
}
